import { Vec3, clamp } from './math.js';
import { SampledSpline, SplinePoint, Frame, makeFrame, Projection } from './spline.js';

export type EnvironmentId =
  | 'neonhaven' // rain-soaked neon megacity
  | 'reactor' // industrial fusion reactor interior
  | 'duneworks' // desert world with ancient alien machinery
  | 'stormline' // storm above a gas giant
  | 'shipyard' // orbital shipyard with moving capital ships
  | 'aurora' // frozen night landscape with auroras
  | 'ascent' // atmosphere-to-space climb
  | 'voidgate'; // surreal void with impossible geometry

export type TrackFeature =
  | { kind: 'boost'; s: number; length: number; lane: number } // s, length normalized 0..1; lane -1..1
  | { kind: 'jump'; s: number; length: number } // surface attraction off (gap/launch)
  | { kind: 'noWalls'; s: number; length: number; side: 'left' | 'right' | 'both' }
  | { kind: 'hazard'; s: number; length: number; lane: number; width: number; type: 'barrier' | 'slick' }
  | { kind: 'zone'; s: number; length: number; name: string }; // named zone hint for environment FX

export interface BranchDef {
  /** Normalized (0..1) arc position on the main line where the branch peels off / rejoins. */
  entryS: number;
  exitS: number;
  points: SplinePoint[];
  name?: string;
}

export interface MedalTimes {
  bronze: number;
  silver: number;
  gold: number;
  author: number;
}

export interface TrackDef {
  id: string;
  name: string;
  tagline: string;
  environment: EnvironmentId;
  difficulty: 1 | 2 | 3 | 4; // beginner..expert
  layout: 'circuit' | 'sprint';
  defaultLaps: number;
  width: number;
  points: SplinePoint[];
  branches?: BranchDef[];
  features: TrackFeature[];
  /** Player level required to race it. */
  unlockLevel: number;
  /** Per-lap medal times in seconds (total time for sprints). */
  medals: MedalTimes;
  /** Procedurally generated tracks carry their seed for sharing. */
  seed?: string;
}

export const MAIN_SPLINE = 0;

export interface TrackProjection extends Projection {
  splineId: number;
  /** Monotonic race progress in meters along the main line (branches map into main-line range). */
  progress: number;
  width: number;
}

export interface BoostPadRuntime {
  splineId: number;
  sStart: number;
  sEnd: number;
  lane: number;
  halfWidth: number;
}

export interface ZoneRuntime {
  sStart: number;
  sEnd: number;
}

export interface HazardRuntime extends ZoneRuntime {
  lane: number;
  halfWidth: number;
  type: 'barrier' | 'slick';
}

export interface NoWallsRuntime extends ZoneRuntime {
  side: 'left' | 'right' | 'both';
}

export interface RacingLine {
  /** Per main-spline sample: preferred lateral offset in meters. */
  offset: Float32Array;
  /** Per main-spline sample: target speed (m/s) for a competent racer. */
  speed: Float32Array;
}

export class Track {
  readonly def: TrackDef;
  readonly main: SampledSpline;
  readonly branches: { spline: SampledSpline; entryS: number; exitS: number }[] = [];
  readonly length: number;
  /** Ordered checkpoint arc positions (meters on main). checkpoints[0] === 0 is the start/finish. */
  readonly checkpoints: number[];
  readonly boostPads: BoostPadRuntime[] = [];
  readonly jumpZones: ZoneRuntime[] = [];
  readonly noWallZones: NoWallsRuntime[] = [];
  readonly hazards: HazardRuntime[] = [];
  readonly racingLine: RacingLine;

  constructor(def: TrackDef) {
    this.def = def;
    this.main = new SampledSpline(def.points, def.layout === 'circuit', def.width);
    this.length = this.main.length;

    for (const b of def.branches ?? []) {
      this.branches.push({
        spline: new SampledSpline(b.points, false, def.width * 0.6),
        entryS: b.entryS * this.length,
        exitS: b.exitS * this.length,
      });
    }

    // checkpoints roughly every 170m, always at least 3 (plus the finish line at 0)
    const cpCount = Math.max(3, Math.round(this.length / 170));
    this.checkpoints = [];
    for (let i = 0; i < cpCount; i++) this.checkpoints.push((i * this.length) / cpCount);

    for (const f of def.features) {
      const sStart = f.s * this.length;
      const sEnd = sStart + f.length * this.length;
      switch (f.kind) {
        case 'boost':
          this.boostPads.push({
            splineId: MAIN_SPLINE,
            sStart,
            sEnd,
            lane: f.lane,
            halfWidth: 3.2,
          });
          break;
        case 'jump':
          this.jumpZones.push({ sStart, sEnd });
          break;
        case 'noWalls':
          this.noWallZones.push({ sStart, sEnd, side: f.side });
          break;
        case 'hazard':
          this.hazards.push({ sStart, sEnd, lane: f.lane, halfWidth: f.width / 2, type: f.type });
          break;
        case 'zone':
          break;
      }
    }

    this.racingLine = computeRacingLine(this);
  }

  spline(id: number): SampledSpline {
    return id === MAIN_SPLINE ? this.main : this.branches[id - 1].spline;
  }

  /** Convert a position on a spline to main-line race progress in meters. */
  progressOf(splineId: number, s: number): number {
    if (splineId === MAIN_SPLINE) return s;
    const b = this.branches[splineId - 1];
    const t = clamp(s / b.spline.length, 0, 1);
    let span = b.exitS - b.entryS;
    if (span < 0) span += this.length; // branch across the finish line
    return this.main.wrapS(b.entryS + span * t);
  }

  private tmpProj: Projection = { s: 0, lateral: 0, height: 0, distSq: 0 };
  private tmpProj2: Projection = { s: 0, lateral: 0, height: 0, distSq: 0 };

  /**
   * Project onto the track, considering the current spline and possible transfer
   * to/from branches near their entry/exit windows.
   */
  project(pos: Vec3, hint: { splineId: number; s: number } | undefined, out: TrackProjection): TrackProjection {
    const curId = hint?.splineId ?? MAIN_SPLINE;
    const cur = this.spline(curId);
    const p = this.tmpProj;
    cur.project(pos, hint?.s, p);
    let bestId = curId;
    let best = p;

    // check candidate splines we could be transitioning onto
    const candidates: number[] = [];
    if (curId === MAIN_SPLINE) {
      for (let i = 0; i < this.branches.length; i++) {
        const b = this.branches[i];
        // near entry window?
        const d = this.mainDelta(p.s, b.entryS);
        if (Math.abs(d) < 100) candidates.push(i + 1);
      }
    } else {
      const b = this.branches[curId - 1];
      // near either end of the branch → candidate main
      if (p.s < 90 || p.s > b.spline.length - 90) candidates.push(MAIN_SPLINE);
      // also allow recovering to main if we've drifted far from the branch
      if (best.distSq > 45 * 45) candidates.push(MAIN_SPLINE);
    }
    for (const id of candidates) {
      const sp = this.spline(id);
      const q = this.tmpProj2;
      const hintS =
        id === MAIN_SPLINE
          ? curId !== MAIN_SPLINE
            ? this.progressOf(curId, p.s)
            : undefined
          : undefined;
      sp.project(pos, hintS, q);
      if (q.distSq < best.distSq - 1) {
        best = q;
        bestId = id;
        // copy since tmpProj2 may be reused
        this.tmpProj.s = q.s;
        this.tmpProj.lateral = q.lateral;
        this.tmpProj.height = q.height;
        this.tmpProj.distSq = q.distSq;
        best = this.tmpProj;
      }
    }

    out.splineId = bestId;
    out.s = best.s;
    out.lateral = best.lateral;
    out.height = best.height;
    out.distSq = best.distSq;
    out.progress = this.progressOf(bestId, best.s);
    const fr = this.tmpFrame;
    this.spline(bestId).frameAt(best.s, fr);
    out.width = fr.width;
    return out;
  }

  private tmpFrame: Frame = makeFrame();

  /** Signed shortest delta from a to b along the main line (respects circuit wrap). */
  mainDelta(a: number, b: number): number {
    let d = b - a;
    if (this.main.closed) {
      const L = this.length;
      d = ((d % L) + L) % L; // normalize to [0, L)
      if (d > L / 2) d -= L; // take the short way around
    }
    return d;
  }

  isInJumpZone(splineId: number, s: number): boolean {
    if (splineId !== MAIN_SPLINE) return false;
    for (const z of this.jumpZones) if (s >= z.sStart && s <= z.sEnd) return true;
    return false;
  }

  wallsAt(splineId: number, s: number): { left: boolean; right: boolean } {
    if (splineId !== MAIN_SPLINE) return { left: true, right: true };
    let left = true,
      right = true;
    for (const z of this.noWallZones) {
      if (s >= z.sStart && s <= z.sEnd) {
        if (z.side === 'left' || z.side === 'both') left = false;
        if (z.side === 'right' || z.side === 'both') right = false;
      }
    }
    return { left, right };
  }

  /** Grid spawn pose for slot i (0-based). Returns position + frame at that point. */
  spawnPose(slot: number, out: { pos: Vec3; frame: Frame }): void {
    const row = Math.floor(slot / 2);
    const side = slot % 2 === 0 ? -1 : 1;
    let s = -14 - row * 11;
    if (this.main.closed) s = this.main.wrapS(s);
    else s = Math.max(2, 14 + row * 11); // sprints: grid sits after the line facing forward
    this.main.frameAt(s, out.frame);
    out.pos
      .copy(out.frame.pos)
      .addScaled(out.frame.right, side * Math.min(5.5, out.frame.width * 0.22))
      .addScaled(out.frame.up, 1.4);
  }
}

/** Precompute a competent racing line: lateral offsets that cut apexes + speed targets. */
function computeRacingLine(track: Track): RacingLine {
  const sp = track.main;
  const n = sp.count;
  const offset = new Float32Array(n);
  const speed = new Float32Array(n);
  const frame = makeFrame();

  const A_LAT = 62; // max lateral accel m/s^2 for line planning
  const A_BRAKE = 34;
  const V_MAX = 118;

  // 1. raw apex-cutting offset from signed curvature, smoothed
  const raw = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const s = i * sp.spacing;
    sp.frameAt(s, frame);
    const k = sp.signedCurvatureAt(s, frame);
    const halfW = frame.width / 2 - 2.5;
    // cut toward inside of the corner
    raw[i] = clamp(-k * 900, -halfW, halfW);
  }
  // box smooth a few passes
  const tmp = new Float32Array(n);
  for (let pass = 0; pass < 4; pass++) {
    for (let i = 0; i < n; i++) {
      const a = raw[(i - 2 + n) % n];
      const b = raw[(i - 1 + n) % n];
      const c = raw[i];
      const d = raw[(i + 1) % n];
      const e = raw[(i + 2) % n];
      tmp[i] = (a + b + c + d + e) / 5;
    }
    raw.set(tmp);
  }
  offset.set(raw);

  // 2. speed targets from curvature
  for (let i = 0; i < n; i++) {
    const k = Math.max(sp.curvatureAt(i * sp.spacing), 1e-4);
    speed[i] = Math.min(V_MAX, Math.sqrt(A_LAT / k));
  }
  // 3. backward pass for braking anticipation (two loops around for circuits)
  const loops = sp.closed ? 2 : 1;
  for (let pass = 0; pass < loops; pass++) {
    for (let i = n - 1; i >= 0; i--) {
      const j = (i + 1) % n;
      const allowed = Math.sqrt(speed[j] * speed[j] + 2 * A_BRAKE * sp.spacing);
      if (speed[i] > allowed) speed[i] = allowed;
    }
  }
  return { offset, speed };
}
